import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { FileText, Shield, ExternalLink, Loader2 } from 'lucide-react';

const LegalModal = ({ isOpen, onClose, initialTab = 'terms' }) => {
  const [activeTab, setActiveTab] = useState(initialTab);

  // Terms of Service content
  const termsContent = (
    <div className="space-y-6 text-sm text-muted-foreground">
      <section>
        <h3 className="text-lg font-semibold text-foreground mb-3">1. Acceptance of Terms</h3>
        <p>
          By accessing and using GRC Nexus ("the Service"), you accept and agree to be bound by these Terms of Service. 
          If you do not agree to these terms, please do not use the Service.
        </p>
      </section>

      <section>
        <h3 className="text-lg font-semibold text-foreground mb-3">2. Description of Service</h3>
        <p>
          GRC Nexus is a comprehensive Governance, Risk, and Compliance platform that provides tools for:
        </p>
        <ul className="list-disc list-inside mt-2 space-y-1">
          <li>Security compliance management and framework mapping</li>
          <li>Risk assessment and vulnerability scanning</li>
          <li>Document and evidence management</li>
          <li>Cloud security posture management (CSPM)</li>
          <li>OSINT (Open Source Intelligence) toolkit</li>
        </ul>
      </section>

      <section>
        <h3 className="text-lg font-semibold text-foreground mb-3">3. User Accounts</h3>
        <p>
          You are responsible for maintaining the confidentiality of your account credentials and for all activities 
          that occur under your account. You must notify us immediately of any unauthorized use of your account.
        </p>
      </section>

      <section>
        <h3 className="text-lg font-semibold text-foreground mb-3">4. Acceptable Use</h3>
        <p>You agree not to:</p>
        <ul className="list-disc list-inside mt-2 space-y-1">
          <li>Use the Service for any unlawful purpose</li>
          <li>Attempt to gain unauthorized access to any systems or networks</li>
          <li>Interfere with or disrupt the Service</li>
          <li>Upload malicious code or content</li>
          <li>Share your account credentials with others</li>
        </ul>
      </section>

      <section>
        <h3 className="text-lg font-semibold text-foreground mb-3">5. Data and Privacy</h3>
        <p>
          Your use of the Service is also governed by our Privacy Policy. By using the Service, you consent 
          to the collection and use of data as described in our Privacy Policy.
        </p>
      </section>

      <section>
        <h3 className="text-lg font-semibold text-foreground mb-3">6. Intellectual Property</h3>
        <p>
          The Service and its original content, features, and functionality are owned by Beskar IT and are 
          protected by international copyright, trademark, patent, trade secret, and other intellectual property laws.
        </p>
      </section>

      <section>
        <h3 className="text-lg font-semibold text-foreground mb-3">7. Limitation of Liability</h3>
        <p>
          The Service is provided "as is" without warranties of any kind. We shall not be liable for any 
          indirect, incidental, special, consequential, or punitive damages resulting from your use of the Service.
        </p>
      </section>

      <section>
        <h3 className="text-lg font-semibold text-foreground mb-3">8. Changes to Terms</h3>
        <p>
          We reserve the right to modify these terms at any time. We will notify users of any material changes 
          via email or through the Service. Continued use of the Service after changes constitutes acceptance.
        </p>
      </section>

      <section>
        <h3 className="text-lg font-semibold text-foreground mb-3">9. Contact Information</h3>
        <p>
          For questions about these Terms of Service, please contact us at:
          <br />
          <a href="mailto:support@beskarit.com" className="text-primary hover:underline">support@beskarit.com</a>
        </p>
      </section>

      <p className="text-xs text-muted-foreground pt-4 border-t">
        Last updated: July 2025
      </p>
    </div>
  );

  // Privacy Policy content
  const privacyContent = (
    <div className="space-y-6 text-sm text-muted-foreground">
      <section>
        <h3 className="text-lg font-semibold text-foreground mb-3">1. Information We Collect</h3>
        <p>We collect information you provide directly to us, including:</p>
        <ul className="list-disc list-inside mt-2 space-y-1">
          <li><strong>Account Information:</strong> Name, email address, organization name, country</li>
          <li><strong>Usage Data:</strong> How you interact with our Service, features you use</li>
          <li><strong>Security Data:</strong> Scan results, compliance reports, and documents you upload</li>
          <li><strong>Technical Data:</strong> IP address, browser type, device information</li>
        </ul>
      </section>

      <section>
        <h3 className="text-lg font-semibold text-foreground mb-3">2. How We Use Your Information</h3>
        <p>We use the information we collect to:</p>
        <ul className="list-disc list-inside mt-2 space-y-1">
          <li>Provide, maintain, and improve the Service</li>
          <li>Process and complete transactions</li>
          <li>Send you technical notices and support messages</li>
          <li>Respond to your comments, questions, and requests</li>
          <li>Monitor and analyze trends, usage, and activities</li>
          <li>Detect, investigate, and prevent fraudulent transactions and abuse</li>
        </ul>
      </section>

      <section>
        <h3 className="text-lg font-semibold text-foreground mb-3">3. Cookies and Tracking</h3>
        <p>We use the following types of cookies:</p>
        <ul className="list-disc list-inside mt-2 space-y-1">
          <li><strong>Essential Cookies:</strong> Required for the Service to function properly</li>
          <li><strong>Session Cookies:</strong> Keep you logged in during your visit</li>
          <li><strong>Marketing Cookies:</strong> Help us understand how users engage with the Service (optional)</li>
        </ul>
        <p className="mt-2">
          You can manage your cookie preferences through the cookie consent banner.
        </p>
      </section>

      <section>
        <h3 className="text-lg font-semibold text-foreground mb-3">4. Data Security</h3>
        <p>
          We implement appropriate technical and organizational measures to protect your personal data against 
          unauthorized access, alteration, disclosure, or destruction. This includes encryption, access controls, 
          and regular security assessments.
        </p>
      </section>

      <section>
        <h3 className="text-lg font-semibold text-foreground mb-3">5. Data Retention</h3>
        <p>
          We retain your personal data for as long as your account is active or as needed to provide you services. 
          You can request deletion of your data at any time by contacting us.
        </p>
      </section>

      <section>
        <h3 className="text-lg font-semibold text-foreground mb-3">6. Your Rights</h3>
        <p>Depending on your location, you may have the right to:</p>
        <ul className="list-disc list-inside mt-2 space-y-1">
          <li>Access the personal data we hold about you</li>
          <li>Request correction of inaccurate data</li>
          <li>Request deletion of your data</li>
          <li>Object to processing of your data</li>
          <li>Request data portability</li>
          <li>Withdraw consent at any time</li>
        </ul>
      </section>

      <section>
        <h3 className="text-lg font-semibold text-foreground mb-3">7. Third-Party Services</h3>
        <p>
          Our Service may contain links to third-party websites or services. We are not responsible for the 
          privacy practices of these third parties. We encourage you to read their privacy policies.
        </p>
      </section>

      <section>
        <h3 className="text-lg font-semibold text-foreground mb-3">8. International Transfers</h3>
        <p>
          Your information may be transferred to and processed in countries other than your own. We ensure 
          appropriate safeguards are in place to protect your data in accordance with applicable data protection laws.
        </p>
      </section>

      <section>
        <h3 className="text-lg font-semibold text-foreground mb-3">9. Children's Privacy</h3>
        <p>
          The Service is not intended for children under 16 years of age. We do not knowingly collect personal 
          data from children under 16.
        </p>
      </section>

      <section>
        <h3 className="text-lg font-semibold text-foreground mb-3">10. Contact Us</h3>
        <p>
          For questions about this Privacy Policy or to exercise your rights, please contact:
          <br />
          <strong>Beskar IT</strong>
          <br />
          Email: <a href="mailto:privacy@beskarit.com" className="text-primary hover:underline">privacy@beskarit.com</a>
        </p>
      </section>

      <p className="text-xs text-muted-foreground pt-4 border-t">
        Last updated: July 2025
      </p>
    </div>
  );

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[85vh] p-0 overflow-hidden">
        <DialogHeader className="p-6 pb-0">
          <DialogTitle className="flex items-center gap-2 text-xl">
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
              {activeTab === 'terms' ? (
                <FileText className="w-5 h-5 text-primary" />
              ) : (
                <Shield className="w-5 h-5 text-primary" />
              )}
            </div>
            Legal Information
          </DialogTitle>
          <DialogDescription>
            Please review our Terms of Service and Privacy Policy
          </DialogDescription>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <div className="px-6">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="terms" className="flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Terms of Service
              </TabsTrigger>
              <TabsTrigger value="privacy" className="flex items-center gap-2">
                <Shield className="w-4 h-4" />
                Privacy Policy
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="terms" className="mt-0">
            <ScrollArea className="h-[50vh] px-6 py-4">
              {termsContent}
            </ScrollArea>
          </TabsContent>

          <TabsContent value="privacy" className="mt-0">
            <ScrollArea className="h-[50vh] px-6 py-4">
              {privacyContent}
            </ScrollArea>
          </TabsContent>
        </Tabs>

        <div className="p-6 pt-0 flex flex-col sm:flex-row gap-3 border-t bg-muted/30">
          <Button
            variant="outline"
            onClick={onClose}
            className="flex-1"
          >
            Close
          </Button>
          <Button
            variant="outline"
            onClick={() => {
              window.open(
                activeTab === 'terms' 
                  ? 'https://www.beskarit.com/terms-conditions' 
                  : 'https://www.beskarit.com/privacypolicy',
                '_blank'
              );
            }}
            className="flex-1 gap-2"
          >
            <ExternalLink className="w-4 h-4" />
            View Full {activeTab === 'terms' ? 'Terms' : 'Policy'} on Website
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default LegalModal;
